import React, { useState, useEffect } from "react";
import axios from "axios";
import { Loader2, TrendingUp, BarChart3, Clock, ArrowDown } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
import { fmtUSD } from "../utils/currency";
const fmt$ = (v) => fmtUSD(v);

const FUNNEL_COLORS = {
  intake: "bg-blue-500", underwriting: "bg-indigo-500", approved: "bg-emerald-500",
  capital_matching: "bg-teal-500", terms: "bg-amber-500", funding: "bg-orange-500",
  funded: "bg-green-600", declined: "bg-red-400",
};
const FUNNEL_LABELS = {
  intake: "Intake", underwriting: "Underwriting", approved: "Approved",
  capital_matching: "Capital Matching", terms: "Terms", funding: "Funding",
  funded: "Funded", declined: "Declined/Closed",
};

export default function AdminFunnelAnalytics() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("ic_token");

  useEffect(() => {
    axios.get(`${API}/analytics/funnel`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setData(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-emerald-600" /></div>;
  if (!data) return null;

  const maxFunnel = Math.max(...(data.funnel || []).map(f => f.count), 1);

  return (
    <div className="space-y-6" data-testid="admin-funnel-analytics">
      {/* Conversion Funnel */}
      <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 sm:p-5" data-testid="conversion-funnel">
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="h-4 w-4 text-emerald-600" />
          <h3 className="text-sm font-bold text-[#222]">Conversion Funnel</h3>
          <span className="text-[9px] text-[#999] ml-auto">{data.total_deals} total deals</span>
        </div>
        <div className="space-y-1.5">
          {(data.funnel || []).map((f, i) => {
            const pct = Math.round((f.count / maxFunnel) * 100);
            const color = FUNNEL_COLORS[f.stage] || "bg-gray-400";
            const label = FUNNEL_LABELS[f.stage] || f.stage;
            return (
              <div key={f.stage}>
                <div className="flex items-center gap-3">
                  <span className="text-[10px] font-medium text-[#666] w-28 truncate">{label}</span>
                  <div className="flex-1 h-5 rounded bg-[#f5f0e8] overflow-hidden relative">
                    <div className={`h-full rounded ${color} transition-all duration-700`} style={{ width: `${Math.max(pct, 3)}%` }} />
                    <span className="absolute inset-0 flex items-center justify-center text-[9px] font-bold text-[#333]">{f.count}</span>
                  </div>
                </div>
                {i < (data.funnel || []).length - 1 && i < 6 && (
                  <div className="flex items-center ml-12 py-0.5">
                    <ArrowDown className="h-3 w-3 text-[#ccc]" />
                    {f.count > 0 && (data.funnel || [])[i + 1]?.count > 0 && (
                      <span className="text-[8px] text-[#aaa] ml-1">{Math.round(((data.funnel || [])[i + 1].count / f.count) * 100)}% conversion</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Monthly Volume Trend */}
      {data.monthly_trend?.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 sm:p-5" data-testid="monthly-trend">
          <div className="flex items-center gap-2 mb-4">
            <TrendingUp className="h-4 w-4 text-blue-600" />
            <h3 className="text-sm font-bold text-[#222]">Monthly Deal Volume (6 Months)</h3>
          </div>
          <div className="flex items-end gap-2 h-32">
            {data.monthly_trend.map((m, i) => {
              const maxDeals = Math.max(...data.monthly_trend.map(x => x.deals), 1);
              const h = Math.max(Math.round((m.deals / maxDeals) * 100), 4);
              return (
                <div key={i} className="flex-1 flex flex-col items-center gap-1">
                  <span className="text-[9px] font-bold text-[#555]">{m.deals}</span>
                  <div className="w-full rounded-t bg-blue-500/80 transition-all" style={{ height: `${h}%` }} />
                  <span className="text-[8px] text-[#999] truncate max-w-full">{m.month.split(" ")[0]}</span>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Time in Stage */}
      {data.time_in_stage?.length > 0 && (
        <div className="rounded-xl border border-[#e8e0d0] bg-white p-4 sm:p-5" data-testid="time-in-stage">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-amber-600" />
            <h3 className="text-sm font-bold text-[#222]">Avg Time in Stage</h3>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
            {data.time_in_stage.map((ts, i) => (
              <div key={i} className="rounded-lg bg-[#faf7f2] border border-[#e8e0d0] px-3 py-2">
                <div className="text-[9px] text-[#999] uppercase font-bold capitalize">{ts.stage.replace(/_/g, " ")}</div>
                <div className="text-sm font-black text-[#333] mt-0.5">{ts.avg_days}d</div>
                <div className="text-[8px] text-[#aaa]">{ts.count} transitions</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
